import { useEffect, useState } from 'react';
import FormField from '../common/FormField';
import handleChange from '../../utils/formUtils/handleChange';
import { EmailMessageDTO, ReplyTemplateDTO } from '../../services/apiClient';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import Button from '../common/Button';
import { useReplyTemplateApi } from '../../hooks/useReplyTemplateApi';

interface SendEmailFormProps {
  memberId: string;
  recordId?: string;
  componentId: string;
  onSubmit: (data: EmailMessageDTO) => void;
  onCancel: () => void;
  isLoading: boolean;
}

const SendEmailForm: React.FC<SendEmailFormProps> = ({
  memberId,
  recordId,
  componentId,
  onSubmit,
  onCancel,
  isLoading,
}) => {
  const [formData, setFormData] = useState<EmailMessageDTO>({});
  const [replyTemplates, setReplyTemplates] = useState<ReplyTemplateDTO[]>([]);
  const [selectedReplyTemplate, setSelectedReplyTemplate] = useState<ReplyTemplateDTO>({});

  const { search: searchReplyTemplates, isLoading: isLoadingReplyTemplates } =
    useReplyTemplateApi();

  const validationRules: ValidationRules = {
    subject: [{ required: true, message: 'Subject is required' }],
  };

  const { errors, validate } = useFormValidation(validationRules);

  useEffect(() => {
    const fetchReplyTemplates = async () => {
      try {
        const response = await searchReplyTemplates({
          filters: {
            componentId: componentId,
            isActive: true,
          },
          page: 1,
          pageSize: 9999,
          sortBy: 'DisplayPosition',
          sortDescending: false,
        });
        setReplyTemplates(response?.data?.results || []);
      } catch (error) {
        console.error('Error fetching reply templates:', error);
      }
    };
    fetchReplyTemplates();

    setFormData((prev) => ({
      ...prev,
      memberId: memberId,
      recordId: recordId,
      componentId: componentId
    }))
  }, []);

  useEffect(() => {
        const replyTemplate = replyTemplates.find((t) => t.id === selectedReplyTemplate.id);
        if (replyTemplate) {
          setFormData((prev) =>
              ({
                ...prev,
                message: replyTemplate.body
              }),
          );
        }
    }, [selectedReplyTemplate, replyTemplates]);

  const handleSubmit = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  return (
    <>
      <FormField
        id="subject"
        label="Subject"
        type="text"
        value={formData.subject}
        onChange={(e) => handleChange(e, setFormData)}
        placeholder="Enter Subject"
        required={true}
        error={errors.subject}
      />
      <FormField
        id="id"
        label="Feedback Template"
        type="dropdown"
        value={selectedReplyTemplate.id}
        onChange={(e) => handleChange(e, setSelectedReplyTemplate)}
        placeholder="Select Feedback Template"
        required={false}
        options={[...replyTemplates.map((replyTemplate) => ({
          value: replyTemplate.id,
          label: replyTemplate.title
        }))]}
        isLoading={isLoadingReplyTemplates}
      />
      <FormField
        id="message"
        label={`Message to: `}
        type="richtext"
        value={formData.message}
        onChange={(e) => handleChange(e, setFormData)}
        placeholder="Enter Message"
        required={true}
        error={errors.message}
      />
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" variant="primary" onClick={handleSubmit} disabled={isLoading}>
          Send
        </Button>
      </div>
    </>
  );
};

export default SendEmailForm;
